import { useCallback, useEffect, useRef, useState } from "react";
import { api, authFetch } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import type { PaymentAccountSnapshot } from "@/hooks/usePaymentAccountStatus";

interface OnboardingSteps {
  paymentsEnabled: boolean;
  customerCreated: boolean;
  productCreated: boolean;
  invoiceCreated: boolean;
}

interface ArOnboardingStatus {
  loading: boolean;
  steps: OnboardingSteps;
  completedCount: number;
  hasBeenOnboarded: boolean;
  refresh: () => Promise<void>;
}

export function useArOnboardingStatus(): ArOnboardingStatus {
  const { activeOrganisationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState<OnboardingSteps>({
    paymentsEnabled: false,
    customerCreated: false,
    productCreated: false,
    invoiceCreated: false,
  });
  const [hasBeenOnboarded, setHasBeenOnboarded] = useState(false);
  const fetchedRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    if (!activeOrganisationId) return;
    setLoading(true);

    const [orgRes, customersRes, productsRes, invoicesRes, paymentRes] = await Promise.all([
      api.GET("/v1/organisations/{id}", {
        params: { path: { id: activeOrganisationId } },
      }),
      api.GET("/v1/customers", { params: { query: { Limit: 1 } } }),
      api.GET("/v1/products", { params: { query: { Limit: 1 } } }),
      api.GET("/v1/invoices", { params: { query: { Limit: 1 } } }),
      authFetch(`/api/proxy/v1/organisations/${activeOrganisationId}/payment-account/status`),
    ]);

    const org = orgRes.data;
    setHasBeenOnboarded(!!org?.hasBeenOnboarded);

    let paymentsEnabled = false;
    if (paymentRes.ok) {
      const snapshot = (await paymentRes.json()) as PaymentAccountSnapshot;
      paymentsEnabled = snapshot.status === "Active";
    }

    setSteps({
      paymentsEnabled,
      customerCreated: (customersRes.data?.data?.length ?? 0) > 0,
      productCreated: (productsRes.data?.data?.length ?? 0) > 0,
      invoiceCreated: (invoicesRes.data?.data?.length ?? 0) > 0,
    });

    setLoading(false);
  }, [activeOrganisationId]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchStatus();
  }, [fetchStatus]);

  const completedCount = Object.values(steps).filter(Boolean).length;

  return { loading, steps, completedCount, hasBeenOnboarded, refresh: fetchStatus };
}
